import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import DataSource from '../../../../api';
import {
  CoachingDetailsInput,
  CoachingFormData,
  CoachingSessionInput,
  ContactInput,
  defaultCoachingSessionValues,
  enumEmpty,
  StatusOptionsObject,
} from '../../../../api/CoachingForm/schema';
import {
  ConsolidatedContact,
  ContactRecommendationsData,
  GoodRecommendationOptionsObject,
} from '../../../../api/CoachingNotes/schema';
import { convertDetailsToDisplay } from '../../../CoachingForm/CoachingDetails/Container';
import { getAllTopics } from '../../../CoachingForm/CoachingDetails/Data';
import { convertSessionToDisplay } from '../../../CoachingForm/CoachingSession/FormPanel';
import { convertReviewToDisplay } from '../../../CoachingForm/ContactReview/Container';
import { getLabelFromValue } from '../../../CoachingForm/FieldComponents/Helpers';
import FieldArrayDisplay from '../../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import trimCoachingData from '../../../CoachingForm/trimCoachingData';

// Specific for one coaching note
const sortDetail = (note: CoachingFormData, intl: IntlShape): void => {
  if ('coachingDetails' in note) {
    note.coachingDetails.sort(
      (a: CoachingDetailsInput, b: CoachingDetailsInput) => {
        if (
          getLabelFromValue(a.coachingTopic, getAllTopics(intl)) <
          getLabelFromValue(b.coachingTopic, getAllTopics(intl))
        ) {
          return -1;
        }
        if (
          getLabelFromValue(a.coachingTopic, getAllTopics(intl)) >
          getLabelFromValue(b.coachingTopic, getAllTopics(intl))
        ) {
          return 1;
        }
        return 0;
      }
    );
  }
};

interface CoachingFormModalProps {
  parentValues: ConsolidatedContact | ContactRecommendationsData;
  coachingNote: CoachingFormData;
}
const CoachingFormDisplay: React.FC<CoachingFormModalProps> = ({
  parentValues,
  coachingNote,
}: CoachingFormModalProps): React.ReactElement => {
  const intl = useIntl();
  const [, setMarkCompleteState] = useState('');
  const apiNaws = DataSource.getDataFeed('NAWS');
  const mutableCoachingNote = apiNaws.removeKeys(coachingNote, '__typename');
  sortDetail(mutableCoachingNote, intl);

  const noteID = mutableCoachingNote.id;
  let contactReview: ContactInput[] = [];
  let coachingDetails: CoachingDetailsInput[] = [];
  let coachingSession: CoachingSessionInput = defaultCoachingSessionValues;

  if ('contactReview' in mutableCoachingNote)
    contactReview = [...mutableCoachingNote.contactReview];

  if ('coachingDetails' in mutableCoachingNote) {
    coachingDetails = [...mutableCoachingNote.coachingDetails];
  }

  if ('coachingSession' in mutableCoachingNote)
    coachingSession = {
      ...coachingSession,
      ...mutableCoachingNote.coachingSession,
    };

  const isCoachingSessionEmpty: boolean =
    coachingSession.coachingTactic === enumEmpty &&
    coachingSession.duration === enumEmpty &&
    coachingSession.coachingMethod === enumEmpty &&
    coachingSession.preparationTime === enumEmpty &&
    coachingSession.managerNotes.length === 0 &&
    coachingSession.coachingSummaryNotes.length === 0;
  // After sorting, we are going to parse the data to separte structure

  const handleReplace = (
    value: CoachingDetailsInput,
    note: CoachingFormData
  ): void => {
    let filterDetails;
    if ('coachingDetails' in note) {
      filterDetails = note.coachingDetails.filter(
        detail =>
          detail.relatedMetric === value.relatedMetric &&
          detail.behaviors === value.behaviors &&
          detail.assignedAction === value.assignedAction &&
          detail.coachingTopic === value.coachingTopic &&
          detail.relatedContacts === value.relatedContacts &&
          detail.tenets === value.tenets
      );
      if (filterDetails[0] !== undefined) {
        filterDetails[0].status = StatusOptionsObject.CLOSED;
      }
    }
    const newValues =
      'coachingDetails' in note ? JSON.parse(JSON.stringify(note)) : null;
    const trimmedValue =
      'coachingDetails' in note && newValues !== null
        ? trimCoachingData(newValues)
        : null;
    if (trimmedValue !== null)
      apiNaws.updateCoachingNote(trimmedValue, setMarkCompleteState);
  };

  return (
    <Column>
      <Row>
        <Text>
          <FormattedMessage
            id='cs_perfportal_contact_recom_label_readonly_good_recom_question'
            defaultMessage='Is the contact a good recommendation for coaching?'
          />{' '}
          {parentValues.contactInfo &&
          parentValues.contactInfo.goodRecommendation ===
            GoodRecommendationOptionsObject.YES ? (
            <FormattedMessage id='cs_perfportal_yes' defaultMessage='Yes' />
          ) : (
            <FormattedMessage id='cs_perfportal_no' defaultMessage='No' />
          )}
        </Text>
      </Row>
      <Column>
        {contactReview.length > 0 && (
          <FieldArrayDisplay
            mapKey={`reviewdisplay_${noteID}`}
            displayValues={contactReview.map(review =>
              convertReviewToDisplay(review, intl)
            )}
          />
        )}
        {coachingDetails.length > 0 && (
          <FieldArrayDisplay
            mapKey={`detaildisplay_${noteID}`}
            displayValues={coachingDetails.map(coachingDetail =>
              convertDetailsToDisplay(
                coachingDetail,
                intl,
                detail => handleReplace(detail, mutableCoachingNote),
                true
              )
            )}
          />
        )}
        {!isCoachingSessionEmpty && (
          <FieldArrayDisplay
            mapKey={`sessiondisplay_${noteID}`}
            displayValues={convertSessionToDisplay(coachingSession, intl, true)}
          />
        )}
      </Column>
    </Column>
  );
};

export default CoachingFormDisplay;
